import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtTRY, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { X, ArrowDownCircle, ArrowUpCircle, Banknote, Wallet, CreditCard, Terminal, FileText, Landmark } from "lucide-react";

/**
 * Tahsilat / Ödeme Merkezi (Settlement Modal)
 *
 * Props:
 *  - contact: { id, name, type, balance }
 *  - direction: "collection" | "payment"
 *  - onClose: () => void
 *  - onDone: () => void   (after successful save)
 */
export default function SettlementModal({ contact, direction, onClose, onDone }) {
  const isCollection = direction === "collection";
  const [accounts, setAccounts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [instrument, setInstrument] = useState(isCollection ? "bank" : "bank"); // bank | credit_card | pos | cheque
  const [accountId, setAccountId] = useState("");
  const [date, setDate] = useState(todayISO());
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [allocations, setAllocations] = useState({}); // { invoice_id: amountStr }
  const [chequeData, setChequeData] = useState({ cheque_number: "", bank_name: "", branch: "", due_date: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [{ data: accs }, { data: invs }] = await Promise.all([
        api.get("/accounts"),
        api.get(`/invoices?contact_id=${contact.id}&direction=${isCollection ? "sales" : "purchase"}`),
      ]);
      setAccounts(accs || []);
      // Sadece açık ve kısmi faturalar
      setInvoices((invs || []).filter((i) => i.status === "open" || i.status === "partial"));
    })();
  }, [contact.id, isCollection]);

  // Instrument değişince hesap filtresi resetle
  useEffect(() => { setAccountId(""); }, [instrument]);

  const filteredAccounts = useMemo(() => {
    if (instrument === "credit_card") return accounts.filter((a) => a.account_type === "credit_card");
    if (instrument === "dbs") return accounts.filter((a) => a.account_type === "dbs");
    if (instrument === "pos" || instrument === "bank") {
      return accounts.filter((a) => a.account_type === "bank" || a.account_type === "cash");
    }
    return accounts;
  }, [accounts, instrument]);

  // Fatura seçim: her satırda kalan tutar auto-fill; sürecek tutarı istediğinde değiştirsin
  const toggleInvoice = (inv) => {
    setAllocations((prev) => {
      const next = { ...prev };
      if (inv.id in next) {
        delete next[inv.id];
      } else {
        const remaining = (inv.amount - (inv.paid_amount || 0)).toFixed(2);
        next[inv.id] = remaining;
      }
      return next;
    });
  };
  const setAllocation = (invId, val) => {
    setAllocations((prev) => ({ ...prev, [invId]: val }));
  };

  const allocationTotal = useMemo(
    () => Object.values(allocations).reduce((s, v) => s + (parseFloat(v) || 0), 0),
    [allocations]
  );

  // Kullanıcı tutarı boşsa allocation total'a çek
  useEffect(() => {
    if (amount === "" && allocationTotal > 0) setAmount(allocationTotal.toFixed(2));
  }, [allocationTotal]); // eslint-disable-line

  const amountNum = parseFloat(amount) || 0;
  const advance = Math.max(0, amountNum - allocationTotal);
  const overAlloc = allocationTotal > amountNum + 0.005;

  const instrumentOptions = isCollection
    ? [
        { key: "bank", label: "Banka / Nakit", icon: Banknote },
        { key: "pos", label: "POS (Kartla)", icon: Terminal },
        { key: "cheque", label: "Çek (Alınan)", icon: FileText },
      ]
    : [
        { key: "bank", label: "Banka / Nakit", icon: Banknote },
        { key: "credit_card", label: "Kredi Kartı", icon: CreditCard },
        { key: "dbs", label: "DBS", icon: Landmark },
        { key: "cheque", label: "Çek (Verilen)", icon: FileText },
      ];

  const submit = async (e) => {
    e.preventDefault();
    if (instrument === "cheque") {
      if (!chequeData.cheque_number.trim() || !chequeData.bank_name.trim() || !chequeData.due_date) {
        return toast.error("Çek no, banka ve vade tarihi zorunlu");
      }
    } else if (!accountId) {
      return toast.error("Hesap seçin");
    }
    if (amountNum <= 0) return toast.error("Tutar girin");
    if (overAlloc) return toast.error("Fatura dağılımı toplam tutarı aşıyor");
    setSaving(true);
    try {
      const alloc = Object.entries(allocations)
        .map(([invoice_id, v]) => ({ invoice_id, amount: parseFloat(v) || 0 }))
        .filter((a) => a.amount > 0);
      const payload = {
        direction,
        instrument: instrument === "cheque" ? "cheque" : "account",
        date,
        amount: amountNum,
        description: description.trim() || undefined,
        allocations: alloc,
      };
      if (instrument === "cheque") {
        payload.cheque = {
          cheque_number: chequeData.cheque_number.trim(),
          bank_name: chequeData.bank_name.trim(),
          branch: chequeData.branch.trim() || undefined,
          due_date: chequeData.due_date,
          issue_date: date,
        };
      } else {
        payload.account_id = accountId;
      }
      const { data } = await api.post(`/contacts/${contact.id}/settle`, payload);
      const fifoNote = data.fifo_applied ? ` · ${data.updated_invoices?.length || 0} açık fatura otomatik kapatıldı (vade sırasına göre)` : "";
      const advNote = data.unallocated_amount > 0 ? ` (avans: ${fmtTRY(data.unallocated_amount)})` : "";
      toast.success(
        (isCollection ? "Tahsilat kaydedildi" : "Ödeme kaydedildi") + advNote + fifoNote
      );
      onDone && onDone();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto"
      data-testid="settlement-modal"
    >
      <form
        onSubmit={submit}
        className="bg-white border border-black w-full max-w-2xl my-8"
      >
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <div className="flex items-center gap-3">
            {isCollection ? (
              <ArrowDownCircle size={22} className="text-emerald-700" />
            ) : (
              <ArrowUpCircle size={22} className="text-rose-600" />
            )}
            <div>
              <div className="label-caps">{isCollection ? "Tahsilat" : "Ödeme"}</div>
              <h2 className="font-display text-xl font-bold" data-testid="settlement-contact-name">
                {contact.name}
              </h2>
              <div className="text-xs text-neutral-500 mt-0.5">
                Mevcut bakiye:{" "}
                <span className={`font-mono-num font-semibold ${
                  contact.balance > 0 ? "text-emerald-700" : contact.balance < 0 ? "text-rose-600" : ""
                }`}>
                  {fmtTRY(contact.balance || 0)}
                </span>
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="settlement-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Instrument */}
          <div>
            <div className="label-caps mb-2">Ödeme Aracı</div>
            <div className="grid grid-cols-2 gap-2">
              {instrumentOptions.map((opt) => {
                const Icon = opt.icon;
                const active = instrument === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    data-testid={`settlement-instr-${opt.key}`}
                    onClick={() => setInstrument(opt.key)}
                    className={`flex items-center gap-2 px-3 py-2.5 border text-sm transition-all ${
                      active
                        ? "bg-black text-white border-black"
                        : "border-black/15 hover:border-black/40"
                    }`}
                  >
                    <Icon size={16} />
                    <span className="uppercase tracking-widest text-xs font-semibold">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Hesap seçimi veya Çek formu */}
          {instrument === "cheque" ? (
            <div className="space-y-3 border border-black/10 p-3 bg-neutral-50">
              <div className="label-caps">{isCollection ? "Alınan Çek Bilgileri" : "Verilen Çek Bilgileri"}</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-neutral-500 uppercase tracking-widest">Çek No *</label>
                  <input
                    required
                    data-testid="settlement-cheque-number"
                    value={chequeData.cheque_number}
                    onChange={(e) => setChequeData({ ...chequeData, cheque_number: e.target.value })}
                    className="input bg-white w-full mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs text-neutral-500 uppercase tracking-widest">Banka *</label>
                  <input
                    required
                    data-testid="settlement-cheque-bank"
                    value={chequeData.bank_name}
                    onChange={(e) => setChequeData({ ...chequeData, bank_name: e.target.value })}
                    className="input bg-white w-full mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs text-neutral-500 uppercase tracking-widest">Şube</label>
                  <input
                    data-testid="settlement-cheque-branch"
                    value={chequeData.branch}
                    onChange={(e) => setChequeData({ ...chequeData, branch: e.target.value })}
                    className="input bg-white w-full mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs text-neutral-500 uppercase tracking-widest">Vade *</label>
                  <input
                    type="date"
                    required
                    data-testid="settlement-cheque-due"
                    value={chequeData.due_date}
                    onChange={(e) => setChequeData({ ...chequeData, due_date: e.target.value })}
                    className="input bg-white w-full mt-1"
                  />
                </div>
              </div>
              <div className="text-xs text-neutral-500">
                Çek portföye {isCollection ? "alınan çekler" : "verilen çekler"} listesine kaydedilecek.
              </div>
            </div>
          ) : (
            <div>
              <div className="label-caps mb-2">
                {instrument === "credit_card" ? "Kredi Kartı"
                  : instrument === "dbs" ? "DBS Hesabı"
                  : instrument === "pos" ? "Paranın Yatacağı Banka"
                  : "Banka / Nakit Hesabı"}
              </div>
              <select
                required
                data-testid="settlement-account"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                className="input bg-white w-full"
              >
                <option value="">— Seçin —</option>
                {filteredAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} {a.bank_name ? `· ${a.bank_name}` : ""}
                  </option>
                ))}
              </select>
              {filteredAccounts.length === 0 && (
                <p className="text-xs text-rose-600 mt-1">
                  {instrument === "credit_card" ? "Kredi kartı yok. Önce ekleyin."
                    : instrument === "dbs" ? "DBS hesabı yok. Hesaplar sayfasından ekleyin."
                    : "Uygun hesap bulunamadı."}
                </p>
              )}
            </div>
          )}

          {/* Tarih & Tutar */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="label-caps mb-2">Tarih</div>
              <input
                type="date"
                data-testid="settlement-date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="input bg-white w-full"
              />
            </div>
            <div>
              <div className="label-caps mb-2">Toplam Tutar</div>
              <input
                type="number"
                step="0.01"
                min="0.01"
                data-testid="settlement-amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0,00"
                required
                className="input bg-white w-full font-mono-num"
              />
            </div>
          </div>

          {/* Açık faturalar */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="label-caps">Kapatılacak Faturalar</div>
              <div className="text-xs text-neutral-500">
                {invoices.length} açık {isCollection ? "satış" : "alış"} faturası
              </div>
            </div>
            {invoices.length > 0 && Object.keys(allocations).length === 0 && (
              <div
                data-testid="settlement-fifo-hint"
                className="mb-2 border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs px-3 py-2"
              >
                💡 Fatura seçmezseniz tutar, en yakın vadeli faturadan başlayarak otomatik kapatılır (FIFO).
              </div>
            )}
            {invoices.length === 0 ? (
              <div className="border border-dashed border-black/15 px-4 py-6 text-center text-sm text-neutral-500">
                Açık fatura yok — bu bir avans/serbest kayıt olacak.
              </div>
            ) : (
              <div className="border border-black/10 divide-y divide-black/5 max-h-56 overflow-y-auto">
                {invoices.map((inv) => {
                  const selected = inv.id in allocations;
                  const remaining = (inv.amount - (inv.paid_amount || 0)).toFixed(2);
                  const isOverdue = inv.due_date < todayISO();
                  return (
                    <div
                      key={inv.id}
                      className={`flex items-center gap-3 px-3 py-2 text-sm ${selected ? "bg-neutral-50" : ""}`}
                    >
                      <input
                        type="checkbox"
                        data-testid={`settlement-inv-${inv.id}`}
                        checked={selected}
                        onChange={() => toggleInvoice(inv)}
                        className="w-4 h-4 accent-black"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">
                          #{inv.invoice_number}
                          {inv.status === "partial" && (
                            <span className="ml-2 text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 px-1.5 py-0.5">
                              Kısmi
                            </span>
                          )}
                          {isOverdue && (
                            <span className="ml-2 text-[10px] uppercase tracking-widest bg-rose-100 text-rose-700 px-1.5 py-0.5">
                              Vadesi geçti
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-neutral-500">
                          Vade: {inv.due_date} · Toplam {fmtTRY(inv.amount)} · Kalan{" "}
                          <span className="font-mono-num font-semibold">{fmtTRY(parseFloat(remaining))}</span>
                        </div>
                      </div>
                      {selected && (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max={remaining}
                          data-testid={`settlement-inv-amt-${inv.id}`}
                          value={allocations[inv.id]}
                          onChange={(e) => setAllocation(inv.id, e.target.value)}
                          className="input bg-white w-32 font-mono-num text-right"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Özet */}
          <div className="border border-black/10 p-3 bg-neutral-50 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-neutral-500">Fatura dağılımı</span>
              <span className="font-mono-num">{fmtTRY(allocationTotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-500">
                {overAlloc ? "❌ Fark (aşım)" : advance > 0 ? "Avans / Serbest tutar" : "Fark"}
              </span>
              <span className={`font-mono-num ${overAlloc ? "text-rose-600" : advance > 0 ? "text-amber-700" : ""}`}>
                {fmtTRY(amountNum - allocationTotal)}
              </span>
            </div>
            <div className="flex justify-between font-semibold pt-1 border-t border-black/10">
              <span>Toplam işlem</span>
              <span className="font-mono-num">{fmtTRY(amountNum)}</span>
            </div>
          </div>

          {/* Açıklama */}
          <div>
            <div className="label-caps mb-2">Açıklama (opsiyonel)</div>
            <input
              type="text"
              data-testid="settlement-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Örn. Nisan ayı tahsilatı"
              className="input bg-white w-full"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-black/10 bg-neutral-50">
          <button
            type="button"
            onClick={onClose}
            className="brutal-btn border border-black/15 bg-white px-4 py-2 text-sm hover:border-black/40"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={saving || overAlloc || amountNum <= 0 || (instrument !== "cheque" && !accountId)}
            data-testid="settlement-save"
            className={`brutal-btn px-4 py-2 text-sm text-white flex items-center gap-2 disabled:opacity-40 ${
              isCollection ? "bg-emerald-700 border-emerald-700" : "bg-black border-black"
            }`}
          >
            {saving ? "Kaydediliyor…" : isCollection ? "Tahsilatı Kaydet" : "Ödemeyi Kaydet"}
          </button>
        </div>
      </form>
    </div>
  );
}
